import { useState, useEffect } from 'react';
import { Text, Button, Menu, TextInput } from '@mantine/core';
import { IconTrash, IconCopy, IconWorld, IconDotsVertical, IconNotes, IconExternalLink, IconCashBanknoteMove, IconCalendar, IconFileDollar, IconDeviceFloppy, IconCashPlus, IconMailForward, IconBuildingStore, IconPrinter, IconTicket, IconEditCircle, IconReceiptTax } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { openConfirmModal } from '@mantine/modals';
import { Reservation, Person, Business } from '@turizi/shared';
import BusinessSelector from '@/components/business-selector';
import PayProvidersModal from './reservation/pay-providers-modal';
import SendTripEmailModal from './send-trip-email-modal';
import GenerateReservationTaxReceiptsModal from './reservation/generate-reservation-tax-receipts-modal';
import { localTenant } from '@/services/session-service';
import { useDeleteReservation, useDuplicateReservation, useUpdateReservation } from '@/hooks/api-hooks';
import { RESERVATION_PRINT_PATH, RESERVATIONS_PATH } from '@/lib/navigation';
import { formatDayLongTime } from '@/lib/format-utils';
import EditReservationPayment from './edit-reservation-payment';
import EditTextModal from './edit-text-modal';
import CardModal from './card-modal';
import { DatePickerInput } from '@mantine/dates';
import dayjs from 'dayjs';

interface ReservationMenuProps {
    reservation?: Reservation;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    disabled?: boolean
}

export default function ReservationMenu({
    reservation,
    disabled = false
}: ReservationMenuProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const tenant = localTenant();

    const deleteReservation = useDeleteReservation();
    const duplicateReservation = useDuplicateReservation();
    const updateReservation = useUpdateReservation();

    // const [shareModalOpened, setShareModalOpened] = useState(false);
    const [notesModalOpened, setNotesModalOpened] = useState(false);
    const [externalCodeModalOpened, setExternalCodeModalOpened] = useState(false);
    const [paymentModalOpened, setPaymentModalOpened] = useState(false);
    const [payProvidersModalOpened, setPayProvidersModalOpened] = useState(false);
    const [taxModalOpened, setTaxModalOpened] = useState(false);
    const [generateTaxReceiptsModalOpened, setGenerateTaxReceiptsModalOpened] = useState(false);
    const [dueDateModalOpened, setDueDateModalOpened] = useState(false);
    const [sendTripModalOpened, setSendTripModalOpened] = useState(false);
    const [childReservationModalOpened, setChildReservationModalOpened] = useState(false);
    const [selectedClient, setSelectedClient] = useState<Business | null>(null);

    const [taxIdDraft, setTaxIdDraft] = useState('');
    const [dueDateDraft, setDueDateDraft] = useState('');
    const [externalCodeDraft, setExternalCodeDraft] = useState('');

    useEffect(() => {
        if (taxModalOpened) {
            setTaxIdDraft(reservation?.tax_id || '');
        }
    }, [taxModalOpened, reservation?.tax_id]);

    useEffect(() => {
        if (dueDateModalOpened) {
            setDueDateDraft(reservation?.payment_due_day || '');
        }
    }, [dueDateModalOpened, reservation?.payment_due_day]);

    useEffect(() => {
        if (externalCodeModalOpened) {
            setExternalCodeDraft(reservation?.external_code || '');
        }
    }, [externalCodeModalOpened, reservation?.external_code]);

    const getPublicReservationUrl = () => {
        const baseUrl = tenant?.settings?.store?.url || 'url-not-set';
        return `https://${baseUrl}/trip/${reservation?.slug}`;
    };

    const handleCreateChildReservation = async (client: Person | Business) => {
        if (!reservation?.id) return;
        try {
            const newReservation = await duplicateReservation.mutateAsync({
                id: Number(reservation.id),
                client_id: client.id,
                client_type: client.type
            });
            if (newReservation?.data?.id) {
                setChildReservationModalOpened(false);
                setSelectedClient(null);
                navigate(`${RESERVATIONS_PATH}/${newReservation.data.id}`);
            }
        } catch (error) {
            console.error("Failed to create child reservation", error);
        }
    };

    const handleDeleteClick = (e: any) => {
        if (e) e.stopPropagation();
        if (!reservation?.id) return;
        openConfirmModal({
            title: t('delete_reservation'),
            children: <Text size="xs">{t('confirm_delete')}</Text>,
            labels: { confirm: t('delete'), cancel: t('cancel') },
            confirmProps: { color: 'red' },
            onConfirm: () => {
                deleteReservation.mutate(Number(reservation.id), {
                    onSuccess: () => {
                        navigate(RESERVATIONS_PATH);
                    },
                });
            },
        });
    };

    return (
        <div className="print:hidden">
            <Menu
                withArrow
                shadow="xl"
                radius="md"
                position='bottom-start'
            >
                <Menu.Target>
                    <Button
                        variant="default"
                        size="xs"
                        radius="xl"
                        leftSection={<IconDotsVertical size={14} />}
                    >
                        {t('menu')}
                    </Button>
                </Menu.Target>
                <Menu.Dropdown>
                    <Menu.Item
                        leftSection={<IconWorld size={16} stroke={1.5} />}
                        onClick={() => window.open(getPublicReservationUrl(), '_blank')}
                    >
                        <div className='flex justify-between items-center'>
                            <span className="font-medium text-[13px]">
                                {t('client_view_public')}
                            </span>
                            <IconExternalLink size={12} stroke={1.5} />
                        </div>
                    </Menu.Item>
                    <Menu.Item
                        leftSection={<IconPrinter size={16} stroke={1.5} />}
                        onClick={() => window.open(RESERVATION_PRINT_PATH.replace(':id', reservation.id.toString()), '_blank')}
                    >
                        <div className='flex justify-between items-center'>
                            <span className="font-medium text-[13px]">
                                {t('print_view')}
                            </span>
                        </div>
                    </Menu.Item>
                    <Menu.Item
                        disabled={disabled}
                        leftSection={<IconMailForward size={16} stroke={1.5} />}
                        onClick={() => setSendTripModalOpened(true)}
                    >
                        <span className="font-medium text-[13px]">
                            {t('send_settlement_by_email')}
                        </span>
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                        disabled={disabled}
                        leftSection={<IconNotes size={16} stroke={1.5} className='text-red-300' />}
                        onClick={() => setNotesModalOpened(true)}
                    >
                        <span className="font-medium text-[13px]">{t('notes')}</span>
                    </Menu.Item>
                    <Menu.Item
                        disabled={disabled}
                        leftSection={<IconTicket size={16} stroke={1.5} className='text-blue-400' />}
                        onClick={() => setExternalCodeModalOpened(true)}
                    >
                        <span className="font-medium text-[13px]">{t('add_external_code')}</span>
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                        leftSection={<IconCopy size={16} stroke={1.5} />}
                        onClick={() => {
                            setSelectedClient(null);
                            setChildReservationModalOpened(true);
                        }}
                        disabled={duplicateReservation.isPending}
                    >
                        <span className="font-medium text-[13px]">{t('create_child_reservation')}</span>
                    </Menu.Item>
                    <Menu.Divider />

                    {/* this is the basic ENTER tax receipt number */}
                    <Menu.Item
                        disabled={disabled}
                        leftSection={<IconFileDollar size={16} stroke={1.5} />}
                        onClick={() => setTaxModalOpened(true)}
                    >
                        <span className="font-medium text-[13px]">{t('receipt_number')}</span>
                    </Menu.Item>

                    {/* Generate AFIP tax receipts */}
                    <Menu.Item
                        disabled={disabled}
                        leftSection={<IconReceiptTax size={16} stroke={1.5} className="text-teal-600" />}
                        onClick={() => setGenerateTaxReceiptsModalOpened(true)}
                    >
                        <span className="font-medium text-[13px]">{t('generate_tax_receipts')}</span>
                    </Menu.Item>

                    <Menu.Item
                        disabled={disabled}
                        leftSection={<IconCalendar size={16} stroke={1.5} />}
                        onClick={() => setDueDateModalOpened(true)}
                    >
                        <span className="font-medium text-[13px]">{t('due_date')}</span>
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                        disabled={disabled}
                        leftSection={<IconCashPlus size={16} stroke={1.5} color='#10b981' />}
                        onClick={() => setPaymentModalOpened(true)}
                    >
                        <span className="font-medium text-[13px]">{t('register_payment_from_client')}</span>
                    </Menu.Item>
                    <Menu.Item
                        disabled={disabled}
                        leftSection={<IconBuildingStore size={16} stroke={1.5} color='#f43f5e' />}
                        onClick={() => setPayProvidersModalOpened(true)}
                    >
                        <span className="font-medium text-[13px]">{t('register_payment_to_provider')}</span>
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Label>
                        <div className='flex gap-1 items-center text-xxs'>
                            <IconDeviceFloppy size={16} stroke={1.5} />
                            <div>
                                {reservation?.created_by?.name || '-'}
                            </div>
                            &middot;
                            <div>
                                {formatDayLongTime(reservation?.created_at)}
                            </div>
                        </div>
                    </Menu.Label>
                    <Menu.Divider />
                    <Menu.Item
                        leftSection={<IconTrash size={16} stroke={1.5} />}
                        color="red"
                        onClick={handleDeleteClick}
                        disabled={deleteReservation.isPending || disabled}
                    >
                        <span className="font-medium text-[13px]">{t('delete')}</span>
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>

            {/* <ReservationSharingModal
                reservation={reservation}
                opened={shareModalOpened}
                onClose={() => setShareModalOpened(false)}
            /> */}

            <EditTextModal
                opened={notesModalOpened}
                onClose={() => setNotesModalOpened(false)}
                value={reservation?.observations}
                onChange={(value) => {
                    if (reservation?.id) {
                        updateReservation.mutate({ id: Number(reservation.id), observations: value });
                    }
                }}
                loading={updateReservation.isPending}
            />

            {/* External Code Modal */}
            <CardModal
                withCloseButton={false}
                opened={externalCodeModalOpened}
                onClose={() => setExternalCodeModalOpened(false)}
                header={<div className="font-bold text-sm">{t('external_code')}</div>}
                size="sm"
            >
                <div className="space-y-4">
                    <TextInput
                        label={t('external_code')}
                        placeholder={t('external_code_placeholder')}
                        maxLength={50}
                        value={externalCodeDraft}
                        onChange={(e) => setExternalCodeDraft(e.target.value)}
                        disabled={updateReservation.isPending}
                    />
                    <div className="flex gap-2 justify-end">
                        <Button
                            color="black"
                            size="xs"
                            variant="subtle"
                            onClick={() => setExternalCodeModalOpened(false)}
                            disabled={updateReservation.isPending}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            color="black"
                            size="xs"
                            radius="xl"
                            onClick={() => {
                                if (reservation?.id) {
                                    updateReservation.mutate(
                                        { id: Number(reservation.id), external_code: externalCodeDraft || null },
                                        {
                                            onSuccess: () => {
                                                setExternalCodeModalOpened(false);
                                            }
                                        }
                                    );
                                }
                            }}
                            loading={updateReservation.isPending}
                        >
                            {t('save')}
                        </Button>
                    </div>
                </div>
            </CardModal>

            <EditReservationPayment
                reservation={reservation}
                opened={paymentModalOpened}
                onClose={() => setPaymentModalOpened(false)}
                modalOnly
                onUpdate={async (data) => {
                    if (reservation?.id) {
                        await updateReservation.mutateAsync({ id: Number(reservation.id), ...data });
                    }
                }}
            />

            {/* Tax ID Modal */}
            <CardModal
                withCloseButton={false}
                opened={taxModalOpened}
                onClose={() => setTaxModalOpened(false)}
                header={<div className="font-bold text-sm">{t('receipt_number')}</div>}
                size="sm"
            >
                <div className="space-y-4">
                    <TextInput
                        label={t('receipt_number_label')}
                        placeholder="e.g. 20-12345678-9"
                        value={taxIdDraft}
                        onChange={(e) => setTaxIdDraft(e.target.value)}
                        disabled={updateReservation.isPending}
                    />
                    <div className="flex gap-2 justify-end">
                        <Button
                            color="black"
                            size="xs"
                            variant="subtle"
                            onClick={() => setTaxModalOpened(false)}
                            disabled={updateReservation.isPending}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            color="black"
                            size="xs"
                            radius="xl"
                            onClick={() => {
                                if (reservation?.id) {
                                    updateReservation.mutate(
                                        { id: Number(reservation.id), tax_id: taxIdDraft || null },
                                        {
                                            onSuccess: () => {
                                                setTaxModalOpened(false);
                                            }
                                        }
                                    );
                                }
                            }}
                            loading={updateReservation.isPending}
                        >
                            {t('save')}
                        </Button>
                    </div>
                </div>
            </CardModal>

            {/* Due Date Modal */}
            <CardModal
                opened={dueDateModalOpened}
                onClose={() => setDueDateModalOpened(false)}
                header={<div className="font-bold text-sm">{t('due_date')}</div>}
                size="sm"
            >
                <div className="space-y-4">
                    <DatePickerInput
                        label={t('due_date')}
                        placeholder={t('select_due_date')}
                        value={dueDateDraft ? dayjs(dueDateDraft).toDate() : null}
                        onChange={(date) => {
                            setDueDateDraft(date ? dayjs(date).format('YYYY-MM-DD') : '');
                        }}
                        clearable
                        disabled={updateReservation.isPending}
                    />
                    <div className="flex gap-2 justify-end">
                        <Button
                            color="black"
                            size="xs"
                            variant="subtle"
                            onClick={() => setDueDateModalOpened(false)}
                            disabled={updateReservation.isPending}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            color="black"
                            size="xs"
                            radius="xl"
                            onClick={() => {
                                if (reservation?.id) {
                                    updateReservation.mutate(
                                        { id: Number(reservation.id), payment_due_day: dueDateDraft || null },
                                        {
                                            onSuccess: () => {
                                                setDueDateModalOpened(false);
                                            }
                                        }
                                    );
                                }
                            }}
                            loading={updateReservation.isPending}
                        >
                            {t('save')}
                        </Button>
                    </div>
                </div>
            </CardModal>

            <SendTripEmailModal
                opened={sendTripModalOpened}
                onClose={() => setSendTripModalOpened(false)}
                reservation={reservation}
            />

            {/* Child Reservation Modal */}
            <CardModal
                opened={childReservationModalOpened}
                onClose={() => {
                    setChildReservationModalOpened(false);
                    setSelectedClient(null);
                }}
                header={<div className="font-bold text-sm">{t('select_client_for_child_reservation')}</div>}
                size="sm"
            >
                <div className="space-y-4">
                    <Text size="xs" className="text-gray-500 font-medium">
                        {t('select_client_to_assign_to_new_reservation')}
                    </Text>
                    <BusinessSelector
                        selected={selectedClient}
                        onSelect={(client) => setSelectedClient(client)}
                    />
                    <div className="flex gap-2 justify-end pt-2 border-t border-slate-100">
                        <Button
                            color="black"
                            size="xs"
                            variant="subtle"
                            onClick={() => {
                                setChildReservationModalOpened(false);
                                setSelectedClient(null);
                            }}
                            disabled={duplicateReservation.isPending}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            color="black"
                            size="xs"
                            radius="xl"
                            disabled={!selectedClient}
                            onClick={() => {
                                if (selectedClient) {
                                    handleCreateChildReservation(selectedClient);
                                }
                            }}
                            loading={duplicateReservation.isPending}
                        >
                            {t('create_child_reservation')}
                        </Button>
                    </div>
                </div>
            </CardModal>

            <PayProvidersModal
                reservation={reservation}
                opened={payProvidersModalOpened}
                onClose={() => setPayProvidersModalOpened(false)}
            />

            <GenerateReservationTaxReceiptsModal
                opened={generateTaxReceiptsModalOpened}
                onClose={() => setGenerateTaxReceiptsModalOpened(false)}
                reservation={reservation}
            />
        </div>
    );
}
